import React, { useEffect, useState } from "react"
import { Box, Button, Checkbox, Tooltip, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import { Layout } from "src/components/layout"
import {
  EMPLOYMENT_TYPE,
  Genders,
  PROVINCES,
  SIDE_MENU,
  SNACKBAR_TYPES,
  STATUS,
  USER_TYPES,
} from "src/modules/const"
import { useNavigate, useParams } from "react-router"
import dayjs, { Dayjs } from "dayjs"
import InputDateField from "src/components/atom/InputDateField"
import InputTextField from "src/components/atom/InputTextField"
import SelectField from "src/components/atom/SelectField"
import { validateField, validateFields } from "src/modules/validation"
import { useAuth } from "src/providers/LoginProvider"

import * as yup from "yup"
import "./index.scss"
import {
  AddressService,
  ApiError,
  Company,
  CompanyService,
  Tag,
  TagService,
  UserService,
} from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import { useRecoilState } from "recoil"
import MultiSelectField from "src/components/atom/MultiSelectField"
import BreadCrumb from "src/components/atom/BreadCrumb"
import { Top } from "../top"
import { SelectDataType } from "src/components/atom/SelectDataType"

export const EmployeeRegister = () => {
  const { t } = useTranslation("translation", { keyPrefix: "employee" })

  const [ID, setID] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameKana, setFirstNameKana] = useState<string>("")
  const [lastNameKana, setLastNameKana] = useState<string>("")
  const [gender, setGender] = useState<string>(Genders.MALE)
  const [birthday, setBirthday] = useState<Dayjs | null>(null)
  const [mailAddress, setMailAddress] = useState<string>("")
  const [postCode, setPostCode] = useState<string>("")
  const [province, setProvince] = useState<string>("none")
  const [city, setCity] = useState<string>("")
  const [street, setStreet] = useState<string>("")
  const [building, setBuilding] = useState<string>("")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [joinDate, setJoinDate] = useState<Dayjs>(dayjs(new Date()))
  const [leaveDate, setLeaveDate] = useState<Dayjs | null>(null)
  const [employmentType, setEmploymentType] = useState<string>("none")
  const [tagValues, setTagValues] = useState<number[]>([])
  const [userType, setUserType] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [confirmPassword, setConfirmPassword] = useState<string>("")
  const [comment, setComment] = useState<string>("")
  const [holding, setHolding] = useState<boolean>(true)
  const [otherGender, setOtherGender] = useState<string>("")
  const [otherGenderDisabled, setOtherGenderDisabled] = useState<boolean>(true)
  const [validation, setValidation] = useState<boolean>(false)

  const navigate = useNavigate()
  const { companyId } = useParams()

  const { user } = useAuth()
  const [company, setCompany] = useState<Company>()
  const [tags, setTags] = useState<Tag[]>([])
  useEffect(() => {
    if (user == null) {
      return
    }
    CompanyService.getCompany(user.token, Number(companyId))
      .then((res) => {
        setCompany(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
    TagService.listTags(user.token, Number(companyId))
      .then((res) => {
        setTags(res)
      })
      .catch(() => {
        setShowProgress(false)
      })
  }, [user, companyId])

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const tagSelectFields: SelectDataType[] = tags.map((tag) => {
    return {
      value: tag.id,
      label: tag.tag,
    }
  })

  const changeGender = (g: number) => {
    if (g == 0) {
      setGender(Genders.MALE)
      setOtherGender("")
      setOtherGenderDisabled(true)
    } else if (g == 1) {
      setGender(Genders.FEMALE)
      setOtherGender("")
      setOtherGenderDisabled(true)
    } else {
      setGender(Genders.OTHER)
      setOtherGenderDisabled(false)
    }
  }

  const [idValidationMessage, setIDValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")
  const [passwordConfirmValidationMessage, setPasswordConfirmValidationMessage] =
    useState<string>("")
  const [nameKanaValidationMessage, setNameKanaValidationMessage] = useState<string>("")
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("")

  const handlePostCodeInput = () => {
    if (!user) {
      return
    }
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode, user.token)
      .then((res) => {
        if (res.prefecture) {
          setProvince(res.prefecture as string)
        }

        if (res.city) {
          let city = res.city!
          if (res.townCandidates && res.townCandidates.length >= 1) {
            city = `${city}${res.townCandidates[0]}`
          }
          setCity(city)
        }
      })
      .catch(() => {})
  }

  const checkValidation = async () => {
    let valid = true
    valid = valid && lastName != ""
    valid = valid && firstName != ""
    valid = valid && lastNameKana != ""
    valid = valid && firstNameKana != ""

    try {
      await validateField(
        yup.object().shape({
          id: yup.string().required(t("please_input_id")).max(24, t("id_length_max")),
        }),
        "id",
        ID
      )
      setIDValidationMessage("")
    } catch (err: any) {
      setIDValidationMessage(err.message)
      valid = valid && false
    }

    try {
      await validateFields(
        yup.object().shape({
          firstNameKana: yup.string().matches(/^[\u30A0-\u30FF]*$/, t("please_input_kana_string")),
          lastNameKana: yup.string().matches(/^[\u30A0-\u30FF]*$/, t("please_input_kana_string")),
        }),
        ["firstNameKana", "lastNameKana"],
        [firstNameKana, lastNameKana]
      )
      setNameKanaValidationMessage("")
    } catch (err: any) {
      setNameKanaValidationMessage(err.message)
      valid = valid && false
    }

    try {
      await validateField(
        yup.object().shape({
          mailAddress: yup.string().email(t("please_input_correct_email")),
        }),
        "mailAddress",
        mailAddress
      )
      setEmailValidationMessage("")
    } catch (err: any) {
      setEmailValidationMessage(err.message)
      valid = valid && false
    }

    valid = valid && userType != ""

    if (postCode != "") {
      try {
        await validateField(
          yup.object().shape({
            postCode: yup
              .string()
              .max(7, t("postcode_length_max"))
              .matches(/〒?[0-9]{7}/g, t("please_input_correct_post_code")),
          }),
          "postCode",
          postCode
        )
        setPostCodeValidationMessage("")
      } catch (err: any) {
        setPostCodeValidationMessage(err.message)
        valid = valid && false
      }
    }

    if (phoneNumber != "") {
      try {
        await validateField(
          yup.object().shape({
            phoneNumber: yup
              .string()
              .matches(
                /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
                t("please_input_correct_phone_number")
              ),
          }),
          "phoneNumber",
          phoneNumber
        )
        setPhoneValidationMessage("")
      } catch (err: any) {
        setPhoneValidationMessage(err.message)
        valid = valid && false
      }
    }

    if (password == "" || confirmPassword == "") {
      valid = valid && false
      setPasswordConfirmValidationMessage("")
    } else {
      if (password != confirmPassword) {
        valid = valid && false
        setPasswordConfirmValidationMessage(t("password_password_confirm_different"))
      } else {
        if (password.length < 6) {
          setPasswordConfirmValidationMessage(t("password_length_min"))
        } else {
          setPasswordValidationMessage("")
          setPasswordConfirmValidationMessage("")
        }
      }
    }

    setValidation(valid)
  }

  const handleRegister = () => {
    if (user == null) {
      return
    }
    setShowProgress(true)

    UserService.createUser(
      Number(companyId),
      {
        employeeId: company?.employerId + ID,
        email: mailAddress || undefined,
        lastName: lastName,
        firstName: firstName,
        lastNameKana: lastNameKana,
        firstNameKana: firstNameKana,
        gender: gender,
        genderComment: otherGender,
        birthday: birthday?.isValid() ? (birthday?.format("YYYY-MM-DD") as string) : undefined,
        postCode: postCode == "" ? undefined : postCode,
        province: province == "none" ? undefined : province,
        city: city == "" ? undefined : city,
        address: street == "" ? undefined : street,
        buildingNo: building == "" ? undefined : building,
        phoneNumber: phoneNumber == "" ? undefined : phoneNumber,
        joiningDate: joinDate.format("YYYY-MM-DD"),
        resignDate: leaveDate?.isValid() ? (leaveDate?.format("YYYY-MM-DD") as string) : undefined,
        status: holding ? STATUS.OFFICE : STATUS.RESIGNED,
        employmentType: employmentType == "none" ? undefined : employmentType,
        tagIds: tagValues,
        userType,
        password: password,
        comment: comment,
      },
      user.token
    )
      .then((res) => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("success_register"), type: SNACKBAR_TYPES.SUCCESS })
        navigate(`/company/${companyId}/employee/detail/${res.id}`)
      })
      .catch((error: ApiError) => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: `${t("failed_register")}\n${error.body}`,
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  useEffect(() => {
    if (holding) {
      setLeaveDate(null)
    }
  }, [holding])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.EMPLOYEE_ADD}>
      <BreadCrumb
        paths={[
          {
            name: t("title"),
            link: "",
          },
        ]}
      />
      <Typography className="employee-update-description">{t("register_description")}</Typography>
      <Box className="employee-content-container">
        <Box className="employee-item-container">
          <Tooltip title={t("id_tooltip")} placement="top">
            <Box className="employee-item-title required">{t("ID")}</Box>
          </Tooltip>
          <Box
            className="employee-item-content-container"
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Typography sx={{ mr: "10px" }}>{company?.employerId}</Typography>
            <InputTextField
              text={ID}
              handleText={setID}
              inputStyle={{ width: "300px", height: "54px" }}
              placeholder={t("ID")}
              checkValidation={checkValidation}
              validationMessage={idValidationMessage}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title required">{t("name")}</Box>
          <Box className="employee-item-content-container" sx={{ display: "flex" }}>
            <InputTextField
              text={lastName}
              handleText={setLastName}
              inputStyle={{ width: "300px", height: "54px" }}
              placeholder={t("last_name")}
              checkValidation={checkValidation}
            />
            <InputTextField
              text={firstName}
              handleText={setFirstName}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ ml: "20px" }}
              placeholder={t("first_name")}
              checkValidation={checkValidation}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title required">{t("name_kana")}</Box>
          <Box className="employee-item-content-container" sx={{ display: "flex" }}>
            <InputTextField
              text={lastNameKana}
              handleText={setLastNameKana}
              inputStyle={{ width: "300px", height: "54px" }}
              placeholder={t("last_name_kana")}
              checkValidation={checkValidation}
              validationMessage={nameKanaValidationMessage}
            />
            <InputTextField
              text={firstNameKana}
              handleText={setFirstNameKana}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ ml: "20px" }}
              placeholder={t("first_name_kana")}
              checkValidation={checkValidation}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("gender")}</Box>
          <Box className="employee-item-content-container" sx={{ display: "flex" }}>
            <Checkbox
              color="primary"
              checked={gender == Genders.MALE}
              onChange={() => {
                changeGender(0)
              }}
              sx={{ pl: "0px", ".MuiSvgIcon-root": { color: "#00C298" } }}
              inputProps={{
                "aria-label": "select all desserts",
              }}
            />
            <Typography sx={{ mt: "18px" }}>{t("male")}</Typography>
            <Checkbox
              color="primary"
              checked={gender == Genders.FEMALE}
              onChange={() => {
                changeGender(1)
                checkValidation()
              }}
              sx={{ ml: "35px", ".MuiSvgIcon-root": { color: "#00C298" } }}
              inputProps={{
                "aria-label": "select all desserts",
              }}
            />
            <Typography sx={{ mt: "18px" }}>{t("female")}</Typography>
            <Checkbox
              color="primary"
              checked={gender == Genders.OTHER}
              onChange={() => {
                changeGender(2)
                checkValidation()
              }}
              sx={{ ml: "35px", ".MuiSvgIcon-root": { color: "#00C298" } }}
              inputProps={{
                "aria-label": "select all desserts",
              }}
            />
            <Typography sx={{ mt: "18px" }}>{t("other")}</Typography>
            <InputTextField
              readOnly={otherGenderDisabled}
              text={otherGender}
              handleText={setOtherGender}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ ml: "20px" }}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("birthday")}</Box>
          <Box className="employee-item-content-container">
            <InputDateField
              date={birthday}
              handleDate={setBirthday}
              inputStyle={{ width: "200px", height: "54px" }}
              checkValidation={checkValidation}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("email_address")}</Box>
          <Box className="employee-item-content-container">
            <InputTextField
              text={mailAddress}
              handleText={setMailAddress}
              inputStyle={{ width: "300px", height: "54px" }}
              placeholder={t("email_address_placeholder")}
              checkValidation={checkValidation}
              validationMessage={emailValidationMessage}
            />{" "}
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("post_code")}</Box>
          <Box className="employee-item-content-container">
            <InputTextField
              text={postCode}
              handleText={setPostCode}
              handleFocusOut={handlePostCodeInput}
              inputStyle={{ width: "300px", height: "54px" }}
              description={t("not_input_hypen")}
              placeholder={t("post_code_placeholder")}
              checkValidation={checkValidation}
              validationMessage={postCodeValidationMessage}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("address")}</Box>
          <Box className="employee-item-content-container">
            <SelectField
              selected={province}
              handleSelected={setProvince}
              datas={PROVINCES}
              inputStyle={{ width: "120px", height: "54px" }}
              sx={{ mb: "10px" }}
              placeholder={t("province")}
            />
            <InputTextField
              text={city}
              handleText={setCity}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              placeholder={t("city")}
              checkValidation={checkValidation}
            />
            <InputTextField
              text={street}
              handleText={setStreet}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              placeholder={t("street")}
              checkValidation={checkValidation}
            />
            <InputTextField
              text={building}
              handleText={setBuilding}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              placeholder={t("building")}
              checkValidation={checkValidation}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("phone_number")}</Box>
          <Box className="employee-item-content-container">
            <InputTextField
              text={phoneNumber}
              handleText={setPhoneNumber}
              inputStyle={{ width: "300px", height: "54px" }}
              description={t("not_input_hypen")}
              placeholder={t("phone_number_placeholder")}
              checkValidation={checkValidation}
              validationMessage={phoneValidationMessage}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("join_company_date")}</Box>
          <Box className="employee-item-content-container" sx={{ display: "flex" }}>
            <InputDateField
              date={joinDate}
              handleDate={setJoinDate}
              inputStyle={{ width: "200px", height: "54px" }}
              checkValidation={checkValidation}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("leave_company_date")}</Box>
          <Box className="employee-item-content-container" sx={{ display: "flex" }}>
            <InputDateField
              date={leaveDate}
              handleDate={setLeaveDate}
              inputStyle={{ width: "200px", height: "54px" }}
              checkValidation={checkValidation}
              disabled={holding}
            />
            <Checkbox
              color="primary"
              checked={holding}
              onChange={() => {
                setHolding(!holding)
              }}
              sx={{ ml: "36px", ".MuiSvgIcon-root": { color: "#00C298" } }}
              inputProps={{
                "aria-label": "select all desserts",
              }}
            />
            <Typography sx={{ mt: "18px" }}>{t("in_holding")}</Typography>
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("employment_type")}</Box>
          <Box className="employee-item-content-container">
            <SelectField
              selected={employmentType}
              handleSelected={setEmploymentType}
              datas={EMPLOYMENT_TYPE}
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              placeholder={t("please_select")}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("tag")}</Box>
          <Box className="employee-item-content-container">
            <MultiSelectField
              sx={{ width: "300px", height: "auto", mb: "10px" }}
              inputStyle={{ width: "300px" }}
              datas={tagSelectFields}
              selected={tagValues}
              handleSelected={setTagValues}
              placeholder={t("please_select")}
              checkValidation={checkValidation}
            ></MultiSelectField>
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title required">{t("user_type")}</Box>
          <Box className="employee-item-content-container">
            <SelectField
              selected={userType}
              handleSelected={setUserType}
              datas={
                user.type === "ADMIN"
                  ? USER_TYPES
                  : USER_TYPES.filter((type) => type.value !== "ADMIN")
              }
              inputStyle={{ width: "300px", height: "54px" }}
              sx={{ mb: "10px" }}
              placeholder={t("please_select")}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title required">{t("password")}</Box>
          <Box className="employee-item-content-container">
            <InputTextField
              text={password}
              handleText={setPassword}
              inputStyle={{ width: "300px", height: "54px" }}
              placeholder={t("password")}
              password
              checkValidation={checkValidation}
              validationMessage={passwordValidationMessage}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title required">{t("password_confirm")}</Box>
          <Box className="employee-item-content-container">
            <InputTextField
              text={confirmPassword}
              handleText={setConfirmPassword}
              inputStyle={{ width: "300px", height: "54px" }}
              placeholder={t("password")}
              password
              checkValidation={checkValidation}
              validationMessage={passwordConfirmValidationMessage}
            />
          </Box>
        </Box>
        <Box className="employee-item-container">
          <Box className="employee-item-title">{t("comment")}</Box>
          <Box className="employee-item-content-container">
            <InputTextField
              text={comment}
              handleText={setComment}
              multiline={true}
              inputStyle={{ width: "500px" }}
              placeholder={t("comment")}
              checkValidation={checkValidation}
            />
          </Box>
        </Box>
      </Box>
      <Button
        className="employee-update-save-button"
        sx={{
          ...(!validation
            ? { backgroundColor: "#F3F3F3 !important", color: "#B9B9B9 !important" }
            : {}),
          width: "200px!important",
        }}
        disabled={!validation}
        onClick={() => handleRegister()}
      >
        {t("save")}
      </Button>
      <Button
        className="employee-update-back-button"
        sx={{ width: "200px" }}
        onClick={() => navigate(`/company/${companyId}/employee`)}
      >
        {t("back")}
      </Button>
    </Layout>
  )
}
